import React from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Box, Tabs, Tab } from "@mui/material";
import { CATEGORY_TYPE, GRAPH_TYPE_DAILY } from "../../utils/enums";
import { useLazyGetBookingGraphQuery } from "../../services/dashboard";

type TimeRange = GRAPH_TYPE_DAILY;
type Category = CATEGORY_TYPE;

const BookingsGraph = () => {
  const [selectedCategory, setSelectedCategory] = React.useState<Category>(CATEGORY_TYPE.VENUE);
  const [selectedRange, setSelectedRange] = React.useState<TimeRange>(GRAPH_TYPE_DAILY.DAILY);
  const [graphData, setGraphData] = React.useState<Array<{ name: string; Count: number }>>([]);
  const [triggerBookingGraph, { isFetching }] = useLazyGetBookingGraphQuery();

  const fetchData = React.useCallback(async () => {
    try {
      const res = await triggerBookingGraph({
        from: 1,
        size: 10,
        search: "",
        categoryType: selectedCategory,
        type: selectedRange,
      }).unwrap();
      if (res?.statusCode === 200 && Array.isArray(res.data)) {
        const transformed = res.data.map((item: any) => ({ name: item.name, Count: Number(item.count || 0) }));
        setGraphData(transformed);
      } else {
        setGraphData([]);
      }
    } catch (_e) {
      setGraphData([]);
    }
  }, [selectedCategory, selectedRange, triggerBookingGraph]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categoryTabs: Array<{ key: Category; label: string }> = [
    { key: CATEGORY_TYPE.VENUE, label: "Venue" },
    { key: CATEGORY_TYPE.STORAGE, label: "Storage" },
  ];

  const rangeTabs: Array<{ key: TimeRange; label: string }> = [
    { key: GRAPH_TYPE_DAILY.DAILY, label: "A day" },
    { key: GRAPH_TYPE_DAILY.WEEKLY, label: "A week" },
    { key: GRAPH_TYPE_DAILY.MONTHLY, label: "A month" },
    { key: GRAPH_TYPE_DAILY.YEARLY, label: "A year" },
  ];

  return (
    <div className="cards table_card">
      <div className="cards_header">
        <div className="left_s">
          <h2>Total Bookings</h2>
        </div>
        <div className="right_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={categoryTabs.findIndex((ct) => ct.key === selectedCategory)}
              onChange={(_e, idx) => setSelectedCategory(categoryTabs[idx].key)}
              aria-label="category tabs"
              className="custom_tabs_links"
            >
              {categoryTabs.map((tab, idx) => (
                <Tab key={tab.key} label={tab.label} id={`booking-cat-tab-${idx}`} aria-controls={`booking-cat-tabpanel-${idx}`} />
              ))}
            </Tabs>
          </Box>
          <p>
            {rangeTabs.map((tab) => (
              <span
                key={tab.key}
                onClick={() => setSelectedRange(tab.key)}
                style={{ cursor: "pointer", fontWeight: selectedRange === tab.key ? 700 : 400 }}
              >
                {tab.label}
              </span>
            ))}
          </p>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={graphData} margin={{ top: 20, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Line type="monotone" dataKey="Count" stroke="#e71c57" strokeWidth={2} isAnimationActive={!isFetching} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BookingsGraph;
